import React, { useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled from "styled-components";
import dayjs from "dayjs";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import { State } from "src/state/ducks";
import * as NotificationActions from "src/state/ducks/notification/actions";

import { formatDate } from "src/helpers/UtilityHelper";
import StyleConfig from "src/constants/StyleConfig";
import { DefaultPageProps, HistoryProps } from "src/constants/Interface";

import { DefaultLayout } from "src/components/layouts/DefaultLayout";
import Heading from "src/components/atoms/typo/Heading";
import HomeButton from "src/components/atoms/button/HomeButton";
import { LinkButton } from "src/components/atoms/button/Button";
import SpinnerModal from "src/components/organisms/loading/SpinnerModal";
import OrderBreadList from "src/components/organisms/list/OrderBreadList";
import ConfirmModal from "src/components/organisms/modal/ConfirmModal";

type OwnProps = {
  id: string;
  date: string;
};

type Props = DefaultPageProps &
  HistoryProps &
  OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const OrderBreadSearchListPage = (props: Props) => {
  const {
    id: currentShopId,
    date,
    getDayNotifications,
    removeOrderBreadFromNotification,
    completeRemoveOrderBreadFromNotification,
    isFetching,
    orderBreadsApiError,
    isOrderBreadsDeleted,
    notifications,
  } = props;

  // 日付でデータ取得
  const fetch = useCallback(() => {
    if (!currentShopId || !date) {
      return;
    }
    const day = dayjs(date);
    getDayNotifications(
      currentShopId,
      day.startOf("date").toDate(),
      day.endOf("date").toDate()
    );
  }, [currentShopId, date, getDayNotifications]);

  // 初期表示：URIから取得した日付内の通知を取得
  useEffect(() => {
    fetch();
  }, [fetch]);

  // キャンセル完了モーダルの表示
  useEffect(() => {
    if (isFetching) {
      return;
    }

    if (!isFetching && isOrderBreadsDeleted) {
      confirmAlert({
        customUI: ({ onClose }) => (
          <ConfirmModal
            title="配信のキャンセルが完了しました"
            contentText="配信をキャンセルしました。"
            onClose={onClose}
            closeText="とじる"
          />
        ),
        willUnmount: () => {
          completeRemoveOrderBreadFromNotification();
        },
      });
      return;
    }

    if (orderBreadsApiError && !isOrderBreadsDeleted) {
      completeRemoveOrderBreadFromNotification();
    }
  }, [
    isFetching,
    orderBreadsApiError,
    isOrderBreadsDeleted,
    completeRemoveOrderBreadFromNotification,
  ]);

  // URIから取得した日付
  const currentDate = useMemo(() => formatDate(date, "YYYY年MM月DD日"), [date]);

  const onDelete = useCallback(
    async (id: string) => {
      // データを削除
      confirmAlert({
        customUI: ({ onClose }) => (
          <ConfirmModal
            title="配信キャンセル確認画面"
            contentText="配信をキャンセルしますか？"
            closeText="もどる"
            onClose={onClose}
            submitText="配信をキャンセルする"
            onSubmit={async () => {
              removeOrderBreadFromNotification(id);
              onClose();
            }}
          />
        ),
      });
    },
    [removeOrderBreadFromNotification]
  );

  // 個別のパンが存在しないデータは非表示（削除後再描画処理対応）
  const filteredNotifications = useMemo(
    () => notifications.filter((v) => v.order_breads.length > 0),
    [notifications]
  );

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <button onClick={fetch}>
          <Heading tag="h1" text={`${currentDate}の配信履歴を確認する`} />
        </button>
        <OrderBreadList
          notifications={filteredNotifications}
          onDelete={onDelete}
        />
        <div className="item">
          <LinkButton
            isDisabled={false}
            url={`/shop/${currentShopId}}/search/order_breads`}
            text={"別の日付の配信履歴を確認する"}
          />
        </div>
        <div className="item">
          <HomeButton />
        </div>
      </Container>
    </DefaultLayout>
  );
};

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M20};
  > .lead {
    font-size: 12px;
  }
  > .item {
    margin: ${StyleConfig.HELPER.M24} auto;
  }
`;

const mapStateToProps = (state: State) => ({
  notifications: state.notification.filterByDate.resource,
  isFetching:
    state.notification.filterByDate.isFetching ||
    state.notification.isFetching ||
    state.notification.orderBreads.isFetching,
  error: state.notification.filterByDate.apiError,
  orderBreadsApiError: state.notification.orderBreads.apiError,
  isOrderBreadsDeleted: state.notification.orderBreads.isDeleted,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getDayNotifications: (id: string, startDate: Date, endDate: Date) =>
    dispatch(
      NotificationActions.getDayNotifications({
        shopId: id,
        startDate: startDate,
        endDate: endDate,
        isReservation: false,
      })
    ),
  removeOrderBreadFromNotification: (orderBreadId: string) =>
    dispatch(
      NotificationActions.removeOrderBreadFromNotification(orderBreadId)
    ),
  completeRemoveOrderBreadFromNotification: () =>
    dispatch(NotificationActions.completeRemoveOrderBreadFromNotification()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrderBreadSearchListPage);
